import type { EditorHost } from '@blocksuite/block-std';
import type {
  EdgelessRootService,
  ShapeElementModel,
  SurfaceBlockModel,
} from '@blocksuite/blocks';
import {
  fitContent,
  LayoutType,
  MindmapElementModel,
  MindmapStyle,
} from '@blocksuite/blocks';
import type { Doc } from '@blocksuite/store';
import { getEdgelessService } from '@pique/core/blocksuite/presets/ai/utils/selection-utils';
import type { WorkspacePropertiesAdapter } from '@pique/core/modules/properties/services/adapter';
import {
  PageSystemPropertyId,
  type PendingMindmapNode,
} from '@pique/core/modules/properties/services/schema';

import type { PiqueMindmapElementModel } from '../block-suite-editor/specs/custom/pique/mindmap-element';

const AUGMENTED_DOC_SUFFIX = '-augmented';

interface MaterializePendingMindmapOptions {
  host: EditorHost;
  sourcePageId: string;
  workspacePropertiesAdapter: WorkspacePropertiesAdapter;
}

interface PendingAugmentationValue {
  mindmapTree?: PendingMindmapNode | null;
}

interface EdgelessElementWithGroup {
  readonly group?: unknown;
}

function docOwnsAugmentedContent(doc: Doc): boolean {
  return (
    doc.id.endsWith(AUGMENTED_DOC_SUFFIX) ||
    doc.collection.getDoc(`${doc.id}${AUGMENTED_DOC_SUFFIX}`) === null
  );
}

function readPendingMindmapTree(value: unknown): PendingMindmapNode | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const pending = value as PendingAugmentationValue;
  return pending.mindmapTree ?? null;
}

function materializePendingMindmap(
  host: EditorHost,
  surface: SurfaceBlockModel,
  edgelessService: EdgelessRootService,
  tree: PendingMindmapNode
): void {
  const mindmapId = surface.addElement({
    type: 'mindmap',
    children: tree,
    style: MindmapStyle.ONE,
    layoutType: LayoutType.RIGHT,
  });

  const mindmap = surface.getElementById(
    mindmapId
  ) as PiqueMindmapElementModel | null;
  if (!mindmap) return;

  host.doc.transact(() => {
    mindmap.childElements.forEach(shape => {
      fitContent(shape as ShapeElementModel);
    });
  });

  mindmap.setDetailLevel(2);

  setTimeout(() => {
    if (mindmap.tree) {
      edgelessService.selection.set({
        elements: [mindmap.tree.element.id],
        editing: false,
      });
    }
  }, 100);
}

export function materializePendingMindmapForPage({
  host,
  sourcePageId,
  workspacePropertiesAdapter,
}: MaterializePendingMindmapOptions): boolean {
  if (!docOwnsAugmentedContent(host.doc)) return false;

  const edgelessService = getEdgelessService(host);
  if (!edgelessService) return false;

  const hasMindmap = (
    edgelessService.edgelessElements as readonly EdgelessElementWithGroup[]
  ).some(element => element.group instanceof MindmapElementModel);
  if (hasMindmap) return false;

  const props = workspacePropertiesAdapter.getPageProperties(sourcePageId);
  const pendingValue =
    props?.system?.[PageSystemPropertyId.PendingAugmentation]?.value;
  const tree = readPendingMindmapTree(pendingValue);
  if (!tree) return false;

  const surface = host.doc.getBlocksByFlavour('affine:surface')[0]?.model as
    | SurfaceBlockModel
    | undefined;
  if (!surface) return false;

  materializePendingMindmap(host, surface, edgelessService, tree);

  workspacePropertiesAdapter.ensurePageProperties(sourcePageId);
  const pageProps = workspacePropertiesAdapter.pageProperties?.[sourcePageId];
  const pendingProperty =
    pageProps?.system[PageSystemPropertyId.PendingAugmentation];
  if (pendingProperty) {
    pendingProperty.value = null;
  }

  return true;
}

export async function ensurePendingMindmapMaterialized({
  doc,
  sourcePageId,
  workspacePropertiesAdapter,
}: {
  doc: Doc;
  sourcePageId: string;
  workspacePropertiesAdapter: WorkspacePropertiesAdapter;
}): Promise<boolean> {
  if (!docOwnsAugmentedContent(doc)) return false;

  const props = workspacePropertiesAdapter.getPageProperties(sourcePageId);
  const pendingValue =
    props?.system?.[PageSystemPropertyId.PendingAugmentation]?.value;
  const tree = readPendingMindmapTree(pendingValue);
  if (!tree) return false;

  for (let attempt = 0; attempt < 12; attempt++) {
    const surface = doc.getBlocksByFlavour('affine:surface')[0]?.model as
      | SurfaceBlockModel
      | undefined;
    if (surface) {
      const hasMindmap = surface.elementModels.some(
        element => element instanceof MindmapElementModel
      );
      if (hasMindmap) return true;

      const mindmapId = surface.addElement({
        type: 'mindmap',
        children: tree,
        style: MindmapStyle.ONE,
        layoutType: LayoutType.RIGHT,
      });
      const mindmap = surface.getElementById(
        mindmapId
      ) as PiqueMindmapElementModel | null;
      if (mindmap) {
        doc.transact(() => {
          mindmap.childElements.forEach(shape => {
            fitContent(shape as ShapeElementModel);
          });
        });
        mindmap.setDetailLevel(2);
      }
      return true;
    }
    await new Promise<void>(resolve => setTimeout(resolve, 50));
  }

  return false;
}
